import { useCallback } from 'react';
import {
  QueryClient,
  QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { FindNewCompanyGetDto } from 'crono-fe-common/types/findNew';
import { Response } from 'crono-fe-common/types/response';
import { useLinkedinGetCompanyInfo } from 'crono-fe-common/hooks/crono-extension/gateway';
import {
  isFindNewCompanyComplete,
  mapFindNewCompanyToLinkedinCompany,
} from 'crono-fe-common/utils/findNew/mappers';
import { findNewPaths } from 'crono-fe-common/utils/findNew/requests';
import LinkedinUrlUtils from 'crono-fe-common/utils/LinkedinUrlUtils';
import { FindNewSourceAnalyticsEvent } from 'crono-fe-common/types/analyticsFindNewSource';
import { useAnalytics } from 'context/analytics';
import client from 'utils/clients/client';
import {
  findNewQueryCacheOptions,
  findNewQueryKeys,
  parseFindNewData,
} from './findNewRequest';

const fetchFindNewCompany = async (
  queryClient: QueryClient,
  queryKey: QueryKey,
  path: string,
): Promise<FindNewCompanyGetDto | null> => {
  const response = await queryClient.fetchQuery<Response<FindNewCompanyGetDto>>(
    {
      queryKey,
      queryFn: () => client({ url: path, config: { method: 'get' } }),
      ...findNewQueryCacheOptions,
    },
  );
  return parseFindNewData(response?.data);
};

type CompanyInfoResult = ReturnType<typeof useLinkedinGetCompanyInfo>['result'];

type CallParams = Parameters<
  ReturnType<typeof useLinkedinGetCompanyInfo>['call']
>[0];

export default function useCompanyInfoWithFallback() {
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const {
    call: linkedinCall,
    resetData: linkedinResetData,
    error: linkedinError,
  } = useLinkedinGetCompanyInfo();

  const {
    mutateAsync: call,
    data: result,
    isLoading,
    reset: resetMutation,
  } = useMutation<CompanyInfoResult, unknown, CallParams>({
    // Clear stale LinkedIn state so a fresh DB hit can't surface a prior call's
    // error (the DB phase never sets one).
    onMutate: linkedinResetData,
    mutationFn: async (params) => {
      const idsFromUrl = LinkedinUrlUtils.extractCompanyIdsFromUrl(
        params.url ?? params.company?.url,
      );
      const publicId =
        params.publicId ?? params.company?.publicId ?? idsFromUrl.publicId;
      const numericId =
        params.numericId ?? params.company?.numericId ?? idsFromUrl.numericId;

      try {
        let dto: FindNewCompanyGetDto | null = null;
        if (publicId) {
          dto = await fetchFindNewCompany(
            queryClient,
            findNewQueryKeys.company(publicId),
            findNewPaths.companyByPublicId(publicId),
          );
        } else if (numericId) {
          dto = await fetchFindNewCompany(
            queryClient,
            findNewQueryKeys.companyByNumericId(numericId),
            findNewPaths.companyByNumericId(numericId),
          );
        }

        if (dto != null && isFindNewCompanyComplete(dto)) {
          analytics?.track(FindNewSourceAnalyticsEvent.CompanyInfoInternal);
          const company = mapFindNewCompanyToLinkedinCompany(dto);
          const companyPublicId = company.publicId ?? publicId;
          // The DB DTO carries no url, so the mapper leaves it null. Backfill
          // it from the caller's input (or derive a company url from the slug,
          // else the numeric id) so consumers that persist company.url into
          // account.linkedin don't blank out an existing value.
          return {
            company: {
              ...company,
              publicId: companyPublicId,
              url:
                company.url ??
                params.url ??
                params.company?.url ??
                LinkedinUrlUtils.getLinkedinCompanyUrl(
                  companyPublicId ?? company.numericId ?? numericId,
                ) ??
                null,
            },
          };
        }
      } catch {
        // Fall through to LinkedIn
      }

      const linkedinResult = await linkedinCall(params);
      if (linkedinResult?.company != null) {
        analytics?.track(FindNewSourceAnalyticsEvent.CompanyInfoLinkedin);
      }
      return linkedinResult;
    },
  });

  const resetData = useCallback(() => {
    resetMutation();
    linkedinResetData();
  }, [resetMutation, linkedinResetData]);

  return {
    result: result ?? null,
    error: linkedinError,
    isLoading,
    call,
    resetData,
  };
}
